/**
 * UnsavedChangesContext — global guard for editor screens.
 *
 * An editor registers a guard via `useUnsavedEditGuard` that exposes:
 *   - isDirty(): are there unsaved changes (vs the snapshot taken on mount)?
 *   - save():    accept the current state as the new baseline (no rollback).
 *   - discard(): revert localStorage to the baseline + refresh in-memory state.
 *
 * Layout/menu code calls `requestNavigation(performNav)` instead of navigating
 * directly. If a guard is registered and dirty, a confirmation dialog opens
 * and asks the caregiver whether to save or discard before performing the
 * navigation. The dialog text follows the active caregiver UI language.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useCaregiverTranslation } from "@/hooks/useCaregiverTranslation";

export interface UnsavedGuard {
  isDirty: () => boolean;
  save: () => void;
  discard: () => void;
}

interface UnsavedChangesContextValue {
  registerGuard: (guard: UnsavedGuard | null) => void;
  unregisterGuard: (guard: UnsavedGuard) => void;
  requestNavigation: (perform: () => void) => void;
}

const UnsavedChangesContext = createContext<UnsavedChangesContextValue | null>(null);

export function useUnsavedChanges() {
  return useContext(UnsavedChangesContext);
}

export function UnsavedChangesProvider({ children }: { children: React.ReactNode }) {
  // Stack of guards — supports nested editors (e.g. an item edit form inside
  // a board editor). Any guard reporting dirty triggers the dialog.
  const guardsRef = useRef<UnsavedGuard[]>([]);
  const pendingNavRef = useRef<(() => void) | null>(null);
  const [open, setOpen] = useState(false);
  const { isRTL } = useCaregiverTranslation();
  // Limit to he/en (administrative UI) per project rule.
  const lang: "he" | "en" = isRTL ? "he" : "en";

  const registerGuard = useCallback((g: UnsavedGuard | null) => {
    if (!g) return;
    guardsRef.current.push(g);
  }, []);

  const unregisterGuard = useCallback((g: UnsavedGuard) => {
    guardsRef.current = guardsRef.current.filter((x) => x !== g);
  }, []);

  const anyDirty = () => guardsRef.current.some((g) => g.isDirty());

  const requestNavigation = useCallback((perform: () => void) => {
    if (anyDirty()) {
      pendingNavRef.current = perform;
      setOpen(true);
      return;
    }
    perform();
  }, []);

  // Native browser refresh / tab close warning while dirty.
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (anyDirty()) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  const finish = (action: "save" | "discard" | "cancel") => {
    const perform = pendingNavRef.current;
    pendingNavRef.current = null;
    setOpen(false);
    if (action === "cancel" || !perform) return;
    // Apply action to every dirty guard (innermost first), so nested edit
    // forms and their parent editor both resolve consistently.
    const dirtyGuards = [...guardsRef.current].reverse().filter((g) => g.isDirty());
    for (const g of dirtyGuards) {
      if (action === "save") g.save();
      else g.discard();
    }
    perform();
  };

  const labels = lang === "he"
    ? {
        title: "לשמור שינויים?",
        body: "ערכת את הלוח אך לא לחצת על שמירה. מה תרצה לעשות?",
        save: "שמור",
        discard: "לא לשמור",
        cancel: "חזרה לעריכה",
      }
    : {
        title: "Save changes?",
        body: "You edited the board but didn't tap save. What would you like to do?",
        save: "Save",
        discard: "Discard",
        cancel: "Keep editing",
      };

  return (
    <UnsavedChangesContext.Provider value={{ registerGuard, unregisterGuard, requestNavigation }}>
      {children}
      <Dialog open={open} onOpenChange={(v) => { if (!v) finish("cancel"); }}>
        <DialogContent
          className={
            "sm:max-w-md " +
            (lang === "en"
              ? "[&>button.absolute]:right-4 [&>button.absolute]:left-auto"
              : "[&>button.absolute]:left-4 [&>button.absolute]:right-auto")
          }
          dir={lang === "he" ? "rtl" : "ltr"}
        >
          <DialogHeader className={lang === "en" ? "text-left sm:text-left" : undefined}>
            <DialogTitle className={lang === "en" ? "text-left" : undefined}>{labels.title}</DialogTitle>
            <DialogDescription className={lang === "en" ? "text-left" : undefined}>{labels.body}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:justify-end">
            <Button variant="ghost" onClick={() => finish("cancel")}>
              {labels.cancel}
            </Button>
            <Button variant="outline" onClick={() => finish("discard")}>
              {labels.discard}
            </Button>
            <Button onClick={() => finish("save")}>{labels.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </UnsavedChangesContext.Provider>
  );
}
